import { useEffect, useRef } from "react";

import styled, { css, keyframes } from "styled-components/macro";

import { create } from "zustand";

import FnrTypography from "UI/FnrTypography";

import useIsMobile from "Utilities/Hooks/useIsMobile";

const useGreetingStore = create((set) => ({
  hasAnimated: false,
  markAnimated: () => set({ hasAnimated: true }),
  welcomeIsVisible: false,
  setWelcomeIsVisible: (value) => set({ welcomeIsVisible: value }),
}));

const useWelcomeIsVisible = () => useGreetingStore((s) => s.welcomeIsVisible);
const useSetWelcomeIsVisible = () =>
  useGreetingStore((s) => s.setWelcomeIsVisible);
const useMarkAnimated = () => useGreetingStore((s) => s.markAnimated);

const WORD_DURATION = 0.5;
const WORD_START = 1.25;
const WORD_GAP = 0.15;
const LAST_WORD = "Flocknote";
const WELCOME_ANIMATION_COMPLETE = WORD_START + WORD_GAP * 2 + WORD_DURATION;

const LINES = [
  [
    {
      text: "Welcome",
      italic: true,
      delay: WORD_START,
      duration: WORD_DURATION,
    },
    { text: "to", delay: WORD_START + WORD_GAP, duration: WORD_DURATION },
    {
      text: LAST_WORD,
      delay: WORD_START + WORD_GAP * 2,
      duration: WORD_DURATION,
    },
  ],
];

const slideUp = keyframes`
  from { transform: translateY(100%); }
  to { transform: translateY(0); }
`;

const WelcomeToNextFlocknote = () => {
  const isMobile = useIsMobile();
  const lastWordRef = useRef(null);
  // Read once at mount — no subscription, so markAnimated() won't re-render
  // this component and cause a class swap flash.
  const shouldAnimate = useRef(!useGreetingStore.getState().hasAnimated);

  // Fire callbacks when the last word's CSS animation completes
  useEffect(() => {
    if (!shouldAnimate.current) return;
    const el = lastWordRef.current;
    if (!el) return;
    const handler = () => {
      const store = useGreetingStore.getState();
      store.setWelcomeIsVisible(true);
      store.markAnimated();
    };
    el.addEventListener("animationend", handler);
    return () => el.removeEventListener("animationend", handler);
  }, []);

  return (
    <Wrapper $isMobile={isMobile}>
      {LINES.map((words, i) => (
        <Line key={i}>
          {words.map(({ text, italic, delay, duration }) => (
            <WordSpan
              key={text}
              ref={text === LAST_WORD ? lastWordRef : undefined}
              $delay={delay}
              $duration={duration}
              $animated={!shouldAnimate.current}
            >
              <FnrTypography type="header" color="white" italic={italic}>
                {text}
              </FnrTypography>
            </WordSpan>
          ))}
        </Line>
      ))}
    </Wrapper>
  );
};

const Wrapper = styled.div`
  margin-top: auto;
  /* The line can't wrap (the slide-up animation needs one row), so on narrow phones shrink it
     to fit -- the whole line is ~10.5em wide, 11 leaves a little room. */
  font-size: min(clamp(2rem, 5vw, 3.5rem), calc((100vw - 40px) / 11));
  padding: ${({ $isMobile }) => ($isMobile ? "0 20px" : "0")};
`;

const WordSpan = styled.span`
  display: block;
  ${({ $animated, $delay, $duration }) =>
    $animated
      ? ""
      : css`
          will-change: transform;
          transform: translateY(100%);
          animation: ${slideUp} ${$duration}s ease-out ${$delay}s both;
        `}
`;

const Line = styled.div`
  display: flex;
  gap: 0.25em;
  overflow-x: visible;
  overflow-y: clip;
  margin-bottom: -8px;
`;

export {
  useWelcomeIsVisible,
  useSetWelcomeIsVisible,
  useMarkAnimated,
  WELCOME_ANIMATION_COMPLETE,
};
export default WelcomeToNextFlocknote;
